import type { MessageKey } from '@dijitalapp/i18n';
import type { GuardianQuizDto, QuizAnswerPolicy, QuizAttemptDto, QuizAttemptStatus, QuizDetailDto, QuizResultsDto, QuizSummaryDto } from '@dijitalapp/shared';
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { api, errorMessageKey } from '../lib/api';
import { useApp } from '../lib/app-context';
import { useChat } from '../lib/chat-context';
import { DateInput } from './DateInput';

/** Quiz engine UI (ADR-020): teachers build and review; students take one attempt; guardians see scores. */

const STATUS_KEY: Record<QuizAttemptStatus, MessageKey> = {
  not_started: 'quiz.status.not_started',
  in_progress: 'quiz.status.in_progress',
  submitted: 'quiz.status.submitted',
  missing: 'quiz.status.missing',
};

function useRun() {
  const { i18n } = useApp();
  const [error, setError] = useState<string | null>(null);
  const run = useCallback(
    async (fn: () => Promise<unknown>) => {
      setError(null);
      try {
        await fn();
      } catch (err) {
        setError(i18n.t(errorMessageKey(err)));
      }
    },
    [i18n],
  );
  return { error, run };
}

function ErrorLine({ error }: { error: string | null }) {
  return error ? (
    <p className="alert alert-error" role="alert">
      {error}
    </p>
  ) : null;
}

const sep = { borderTop: '1px solid var(--border)', paddingTop: 'var(--space-3)' };

// ---------- teacher ----------

interface DraftQuestion {
  prompt: string;
  options: string[];
  correctIndex: number;
  points: number;
}
const emptyQuestion = (): DraftQuestion => ({ prompt: '', options: ['', ''], correctIndex: 0, points: 1 });

export function TeacherQuizzes({ classId }: { classId: string }) {
  const { i18n } = useApp();
  const { t } = i18n;
  const chat = useChat();
  const { error, run } = useRun();
  const [quizzes, setQuizzes] = useState<QuizSummaryDto[] | null>(null);
  const [editing, setEditing] = useState<QuizDetailDto | 'new' | null>(null);
  const [results, setResults] = useState<{ id: string; data: QuizResultsDto } | null>(null);

  const load = useCallback(async () => setQuizzes((await api<{ quizzes: QuizSummaryDto[] }>('GET', `/classes/${classId}/quizzes`)).quizzes), [classId]);
  useEffect(() => {
    void run(load);
  }, [load, run, chat.learnVersion]);

  const act = (fn: () => Promise<unknown>) => run(async () => (await fn(), await load()));

  if (editing) {
    return <QuizBuilder classId={classId} quiz={editing === 'new' ? null : editing} onDone={() => (setEditing(null), void run(load))} />;
  }

  return (
    <section className="card stack" aria-labelledby={`quizzes-${classId}`}>
      <div className="row">
        <h3 id={`quizzes-${classId}`} style={{ margin: 0 }}>
          {t('quiz.title')}
        </h3>
        <span className="spacer" />
        <button className="btn btn-primary" type="button" onClick={() => setEditing('new')}>
          {t('quiz.new')}
        </button>
      </div>
      <ErrorLine error={error} />
      {quizzes?.length === 0 && <p className="muted">{t('quiz.none')}</p>}
      {quizzes?.map((q) => (
        <div key={q.id} className="stack" style={sep}>
          <div className="row">
            <span style={{ minWidth: 0 }}>
              <strong>{q.title}</strong>{' '}
              <span className="badge">{t(`quiz.state.${q.status}` as MessageKey)}</span>
              <br />
              <span className="muted">
                {t('quiz.meta', { questions: q.questionCount, points: q.maxScore })}
                {q.timeLimitMinutes && ` · ${t('quiz.minutes', { count: q.timeLimitMinutes })}`}
                {q.dueAt && ` · ${t('planner.due', { date: i18n.formatDate(q.dueAt) })}`}
                {q.stats && q.status !== 'draft' && ` · ${t('quiz.stats', { submitted: q.stats.submitted, students: q.stats.students, average: q.stats.averageScore ?? '—' })}`}
              </span>
            </span>
            <span className="spacer" />
            {q.status === 'draft' && (
              <>
                <button className="btn" type="button" onClick={() => void run(async () => setEditing(await api<QuizDetailDto>('GET', `/quizzes/${q.id}`)))}>
                  {t('quiz.edit')}
                </button>
                <button
                  className="btn btn-primary"
                  type="button"
                  onClick={() => {
                    if (window.confirm(t('quiz.publishConfirm'))) void act(() => api('POST', `/quizzes/${q.id}/publish`));
                  }}
                >
                  {t('quiz.publish')}
                </button>
              </>
            )}
            {q.status !== 'draft' && (
              <button
                className="btn"
                type="button"
                aria-expanded={results?.id === q.id}
                onClick={() => (results?.id === q.id ? setResults(null) : void run(async () => setResults({ id: q.id, data: await api<QuizResultsDto>('GET', `/quizzes/${q.id}/results`) })))}
              >
                {t('quiz.results')}
              </button>
            )}
            {q.status === 'published' && (
              <button className="btn" type="button" onClick={() => void act(() => api('POST', `/quizzes/${q.id}/close`))}>
                {t('quiz.close')}
              </button>
            )}
            <button
              className="link-button"
              type="button"
              onClick={() => {
                if (window.confirm(t('quiz.deleteConfirm'))) void act(() => api('DELETE', `/quizzes/${q.id}`));
              }}
            >
              {t('planner.delete')}
            </button>
          </div>
          {results?.id === q.id && <ResultsView data={results.data} />}
        </div>
      ))}
    </section>
  );
}

function ResultsView({ data }: { data: QuizResultsDto }) {
  const { i18n } = useApp();
  const { t } = i18n;
  return (
    <div className="stack" style={{ paddingLeft: 'var(--space-3)' }}>
      {data.students.length === 0 && <p className="muted">{t('learn.noStudents')}</p>}
      {data.students.map((s) => (
        <div key={s.student.id} className="row">
          <span>{s.student.displayName}</span>
          <span className="spacer" />
          <span className="badge">{t(STATUS_KEY[s.status])}</span>
          {s.score !== null && (
            <strong>
              {s.score}/{data.maxScore}
            </strong>
          )}
        </div>
      ))}
      <h4 style={{ margin: 0 }}>{t('quiz.byQuestion')}</h4>
      {data.questions.map((q, i) => (
        <p key={q.id} style={{ margin: 0 }}>
          {i + 1}. {q.prompt} <span className="muted">· {q.correctRate === null ? '—' : t('quiz.correctRate', { rate: q.correctRate })}</span>
        </p>
      ))}
    </div>
  );
}

function QuizBuilder({ classId, quiz, onDone }: { classId: string; quiz: QuizDetailDto | null; onDone: () => void }) {
  const { i18n } = useApp();
  const { t } = i18n;
  const { error, run } = useRun();
  const [title, setTitle] = useState(quiz?.title ?? '');
  const [instructions, setInstructions] = useState(quiz?.instructions ?? '');
  const [due, setDue] = useState(quiz?.dueAt ? quiz.dueAt.slice(0, 10) : '');
  const [limit, setLimit] = useState(quiz?.timeLimitMinutes?.toString() ?? '');
  const [showAnswers, setShowAnswers] = useState<QuizAnswerPolicy>(quiz?.showAnswers ?? 'after_close');
  const [questions, setQuestions] = useState<DraftQuestion[]>(
    quiz?.questions.map((q) => ({ prompt: q.prompt, options: [...q.options], correctIndex: q.correctIndex, points: q.points })) ?? [emptyQuestion()],
  );
  const setQ = (i: number, patch: Partial<DraftQuestion>) => setQuestions(questions.map((q, j) => (j === i ? { ...q, ...patch } : q)));

  const save = (e: FormEvent) => {
    e.preventDefault();
    const body = {
      title,
      instructions: instructions || undefined,
      dueAt: due ? new Date(`${due}T23:59`).toISOString() : null,
      timeLimitMinutes: limit ? Number(limit) : null,
      showAnswers,
      questions,
    };
    void run(async () => {
      await (quiz ? api('PUT', `/quizzes/${quiz.id}`, body) : api('POST', `/classes/${classId}/quizzes`, body));
      onDone();
    });
  };

  return (
    <form className="card stack" onSubmit={save}>
      <div className="row">
        <h3 style={{ margin: 0 }}>{quiz ? t('quiz.edit') : t('quiz.new')}</h3>
        <span className="spacer" />
        <button className="btn" type="button" onClick={onDone}>
          {t('quiz.cancel')}
        </button>
      </div>
      <div className="field">
        <label htmlFor="qz-title">{t('learn.assignmentTitle')}</label>
        <input id="qz-title" className="input" required maxLength={200} value={title} onChange={(e) => setTitle(e.target.value)} />
      </div>
      <div className="field">
        <label htmlFor="qz-ins">{t('learn.instructions')}</label>
        <textarea id="qz-ins" className="input" rows={2} maxLength={2000} value={instructions} onChange={(e) => setInstructions(e.target.value)} />
      </div>
      <div className="row">
        <div className="field">
          <label htmlFor="qz-due">{t('planner.dueDate')}</label>
          <DateInput id="qz-due" value={due} onChange={setDue} />
        </div>
        <div className="field">
          <label htmlFor="qz-limit">{t('quiz.timeLimit')}</label>
          <input id="qz-limit" className="input" type="number" min={1} max={180} style={{ width: 110 }} value={limit} onChange={(e) => setLimit(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="qz-show">{t('quiz.showAnswers')}</label>
          <select id="qz-show" className="input" value={showAnswers} onChange={(e) => setShowAnswers(e.target.value as QuizAnswerPolicy)}>
            {(['after_submit', 'after_close', 'never'] as const).map((p) => (
              <option key={p} value={p}>
                {t(`quiz.policy.${p}` as MessageKey)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {questions.map((q, i) => (
        <fieldset key={i} className="stack" style={{ ...sep, border: 0, margin: 0, padding: 'var(--space-3) 0 0' }}>
          <legend className="field-legend">{t('quiz.question', { n: i + 1 })}</legend>
          <label className="visually-hidden" htmlFor={`qp-${i}`}>
            {t('quiz.prompt')}
          </label>
          <textarea id={`qp-${i}`} className="input" rows={2} required maxLength={2000} placeholder={t('quiz.prompt')} value={q.prompt} onChange={(e) => setQ(i, { prompt: e.target.value })} />
          {q.options.map((o, k) => (
            <div key={k} className="row" style={{ flexWrap: 'nowrap' }}>
              <input
                type="radio"
                name={`correct-${i}`}
                checked={q.correctIndex === k}
                aria-label={t('quiz.markCorrect', { n: k + 1 })}
                onChange={() => setQ(i, { correctIndex: k })}
              />
              <label className="visually-hidden" htmlFor={`qo-${i}-${k}`}>
                {t('quiz.option', { n: k + 1 })}
              </label>
              <input
                id={`qo-${i}-${k}`}
                className="input"
                required
                maxLength={300}
                placeholder={t('quiz.option', { n: k + 1 })}
                value={o}
                onChange={(e) => setQ(i, { options: q.options.map((x, j) => (j === k ? e.target.value : x)) })}
              />
              {q.options.length > 2 && (
                <button
                  className="link-button"
                  type="button"
                  aria-label={t('quiz.removeOption', { n: k + 1 })}
                  onClick={() => setQ(i, { options: q.options.filter((_, j) => j !== k), correctIndex: q.correctIndex === k ? 0 : q.correctIndex > k ? q.correctIndex - 1 : q.correctIndex })}
                >
                  ✕
                </button>
              )}
            </div>
          ))}
          <div className="row">
            {q.options.length < 6 && (
              <button className="btn" type="button" onClick={() => setQ(i, { options: [...q.options, ''] })}>
                {t('quiz.addOption')}
              </button>
            )}
            <label htmlFor={`qpts-${i}`}>{t('quiz.points')}</label>
            <input id={`qpts-${i}`} className="input" type="number" min={1} max={10} style={{ width: 80 }} value={q.points} onChange={(e) => setQ(i, { points: Number(e.target.value) || 1 })} />
            <span className="spacer" />
            {questions.length > 1 && (
              <button className="link-button" type="button" onClick={() => setQuestions(questions.filter((_, j) => j !== i))}>
                {t('quiz.removeQuestion')}
              </button>
            )}
          </div>
        </fieldset>
      ))}
      <p className="hint">{t('quiz.correctHint')}</p>
      <ErrorLine error={error} />
      <div className="row">
        {questions.length < 50 && (
          <button className="btn" type="button" onClick={() => setQuestions([...questions, emptyQuestion()])}>
            {t('quiz.addQuestion')}
          </button>
        )}
        <span className="spacer" />
        <button className="btn btn-primary">{t('quiz.saveDraft')}</button>
      </div>
    </form>
  );
}

// ---------- student ----------

export function StudentQuizzes({ classId }: { classId: string }) {
  const { i18n } = useApp();
  const { t } = i18n;
  const chat = useChat();
  const { error, run } = useRun();
  const [quizzes, setQuizzes] = useState<QuizSummaryDto[] | null>(null);
  const [attempt, setAttempt] = useState<QuizAttemptDto | null>(null);

  const load = useCallback(async () => setQuizzes((await api<{ quizzes: QuizSummaryDto[] }>('GET', `/classes/${classId}/quizzes`)).quizzes), [classId]);
  useEffect(() => {
    void run(load);
  }, [load, run, chat.learnVersion]);

  if (attempt) return <QuizTaker initial={attempt} onClose={() => (setAttempt(null), void run(load))} />;
  if (!quizzes?.length) return null;

  return (
    <section className="card stack" aria-labelledby={`squiz-${classId}`}>
      <h3 id={`squiz-${classId}`}>{t('quiz.title')}</h3>
      <ErrorLine error={error} />
      {quizzes.map((q) => {
        const mine = q.mine!;
        return (
          <div key={q.id} className="row" style={sep}>
            <span style={{ minWidth: 0 }}>
              <strong>{q.title}</strong>
              <br />
              <span className="muted">
                {t('quiz.meta', { questions: q.questionCount, points: q.maxScore })}
                {q.timeLimitMinutes && ` · ${t('quiz.minutes', { count: q.timeLimitMinutes })}`}
                {q.dueAt && ` · ${t('planner.due', { date: i18n.formatDate(q.dueAt) })}`}
              </span>
            </span>
            <span className="spacer" />
            <span className="badge">{t(STATUS_KEY[mine.status])}</span>
            {mine.score !== null && (
              <strong>
                {mine.score}/{q.maxScore}
              </strong>
            )}
            {mine.status === 'not_started' && (
              <button
                className="btn btn-primary"
                type="button"
                onClick={() => {
                  if (!q.timeLimitMinutes || window.confirm(t('quiz.startConfirm', { count: q.timeLimitMinutes })))
                    void run(async () => setAttempt(await api<QuizAttemptDto>('POST', `/quizzes/${q.id}/start`)));
                }}
              >
                {t('quiz.start')}
              </button>
            )}
            {mine.status === 'in_progress' && (
              <button className="btn btn-primary" type="button" onClick={() => void run(async () => setAttempt(await api<QuizAttemptDto>('POST', `/quizzes/${q.id}/start`)))}>
                {t('quiz.resume')}
              </button>
            )}
            {mine.status === 'submitted' && (
              <button className="btn" type="button" onClick={() => void run(async () => setAttempt(await api<QuizAttemptDto>('GET', `/quizzes/${q.id}/attempt`)))}>
                {t('quiz.view')}
              </button>
            )}
          </div>
        );
      })}
    </section>
  );
}

function useCountdown(deadline: string | null, active: boolean): number | null {
  const [left, setLeft] = useState<number | null>(null);
  useEffect(() => {
    if (!deadline || !active) return;
    const tick = () => setLeft(Math.max(0, Date.parse(deadline) - Date.now()));
    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [deadline, active]);
  return deadline && active ? left : null;
}

function QuizTaker({ initial, onClose }: { initial: QuizAttemptDto; onClose: () => void }) {
  const { i18n } = useApp();
  const { t } = i18n;
  const { error, run } = useRun();
  const [attempt, setAttempt] = useState(initial);
  const [answers, setAnswers] = useState<Record<string, number | null>>(() => Object.fromEntries(initial.questions.map((q) => [q.id, q.myAnswer])));
  const done = attempt.submittedAt !== null;
  const left = useCountdown(attempt.deadline, !done);

  const submit = useCallback(
    () => run(async () => setAttempt(await api<QuizAttemptDto>('POST', `/quizzes/${attempt.quizId}/submit`, { answers }))),
    [run, attempt.quizId, answers],
  );
  // When time runs out, hand in what is there (the server still accepts it within its grace period).
  useEffect(() => {
    if (left === 0 && !done) void submit();
  }, [left, done, submit]);

  const mmss = left === null ? null : `${Math.floor(left / 60_000)}:${String(Math.floor((left % 60_000) / 1000)).padStart(2, '0')}`;

  return (
    <section className="card stack" aria-labelledby="quiz-taking">
      <div className="row">
        <h3 id="quiz-taking" style={{ margin: 0 }}>
          {attempt.title}
        </h3>
        <span className="spacer" />
        {mmss && (
          <span className="badge" role="timer" aria-live="off">
            ⏱ {mmss}
          </span>
        )}
        {done && (
          <strong>
            {t('quiz.score', { score: attempt.score ?? 0, max: attempt.maxScore })}
          </strong>
        )}
      </div>
      {attempt.instructions && <p style={{ margin: 0 }}>{attempt.instructions}</p>}
      {done && !attempt.answersVisible && <p className="hint">{t('quiz.answersLater')}</p>}
      {attempt.questions.map((q, i) => (
        <fieldset key={q.id} className="stack" style={{ ...sep, border: 0, margin: 0, padding: 'var(--space-3) 0 0' }}>
          <legend className="field-legend">
            {i + 1}. {q.prompt} <span className="muted">({t('quiz.pointsShort', { count: q.points })})</span>
          </legend>
          {q.options.map((o, k) => {
            const mark = attempt.answersVisible ? (k === q.correctIndex ? ' ✓' : k === q.myAnswer ? ' ✗' : '') : '';
            return (
              <label key={k} className="check">
                <input
                  type="radio"
                  name={`a-${q.id}`}
                  disabled={done}
                  checked={(done ? q.myAnswer : answers[q.id]) === k}
                  onChange={() => setAnswers({ ...answers, [q.id]: k })}
                />
                <span>
                  {o}
                  {mark && <strong>{mark}</strong>}
                </span>
              </label>
            );
          })}
        </fieldset>
      ))}
      <ErrorLine error={error} />
      <div className="row">
        <button className="btn" type="button" onClick={onClose}>
          {t('learn.back')}
        </button>
        <span className="spacer" />
        {!done && (
          <button
            className="btn btn-primary"
            type="button"
            onClick={() => {
              const blank = attempt.questions.filter((q) => answers[q.id] === null || answers[q.id] === undefined).length;
              if (!blank || window.confirm(t('quiz.blankConfirm', { count: blank }))) void submit();
            }}
          >
            {t('quiz.submit')}
          </button>
        )}
      </div>
      {!done && <p className="hint">{t('quiz.oneAttempt')}</p>}
    </section>
  );
}

// ---------- guardian ----------

export function GuardianQuizList({ quizzes }: { quizzes: GuardianQuizDto[] }) {
  const { i18n } = useApp();
  const { t } = i18n;
  if (!quizzes.length) return null;
  return (
    <>
      <h3 style={{ margin: 0 }}>{t('quiz.title')}</h3>
      {quizzes.map((q) => (
        <div key={q.id} className="toggle-row">
          <span style={{ minWidth: 0 }}>
            <strong>{q.title}</strong> <span className="muted">· {q.className}</span>
            {q.dueAt && <span className="muted"> · {t('planner.due', { date: i18n.formatDate(q.dueAt) })}</span>}
          </span>
          <span className="spacer" />
          {q.score !== null && (
            <span className="badge">
              {q.score}/{q.maxScore}
            </span>
          )}
          <span className="badge">{t(STATUS_KEY[q.status])}</span>
        </div>
      ))}
    </>
  );
}
